import { useCallback } from 'react';
import type React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { scheduledTransactionsActions } from 'app-components/flux/scheduled-transactions';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsUtils } from 'app-components/flux/transactions';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import { getLogger, tracker } from 'app-components/utils/core';

import type { ResourceTxnAction, TransactionDetailsMPProps } from '../types';
import { workflowActions, type WorkflowAction } from '../workflowReducer';
import type { transactionDetailsFormHooks } from './formHooks';

const log = getLogger('component/Dialogs/TransactionEditDialog/hooks/useRecurringTransactionActions.ts');

export type SaveTransactions = (
  txns: CashFlowTransaction[],
  options?: {
    txnOverrides?: {
      isExcludedFromF2S?: boolean | null;
      isExcludedFromReports?: boolean | null;
    };
  },
  resolve?: (resources: ResourceTxnAction[]) => void,
  reject?: (error: unknown) => void,
) => void;

export type RecurringTransactionWorkflowParams = {
  businessId: string | null | undefined;
  closeAll: () => void;
  create: boolean;
  currentTxn: CashFlowTransaction;
  dispatch: (action: unknown) => void;
  excludeF2S: boolean | null | undefined;
  excludeReports: boolean | null | undefined;
  getTransactionDetailsMPProps: (props: TransactionDetailsMPProps) => object;
  isDirty: boolean;
  manager: ReturnType<typeof transactionDetailsFormHooks.useManager>;
  modelIdToLink: string | null | undefined;
  performTransactionAction: (action: { action: string; id: string; otherId?: string }, options?: unknown) => void;
  recurring: boolean | string | undefined;
  resetToEdit: () => void;
  saveTransactions: SaveTransactions;
  setIsBill: (value: boolean) => void;
  showUnsavedRecurringDialog: () => void;
  txn: CashFlowTransaction;
  workflowDispatch: React.Dispatch<WorkflowAction>;
};

export type RecurringTransactionActions = {
  closeRecurring: (dirty?: boolean, force?: boolean) => void;
  createRecurring: () => void;
  dismissReview: () => void;
  doOneTimeBill: () => void;
  exitRecurringSelect: () => void;
  linkRecurring: (item: ScheduledTransaction) => void;
  linkTxn: (recurringId: string | undefined) => void;
  markRecurring: () => void;
  onCloseReview: () => void;
  saveRecurring: () => void;
  startRecurringSelect: () => void;
  stopRecurring: () => void;
  updateRecurring: () => void;
};

export function useRecurringTransactionActions({
  businessId,
  closeAll,
  create,
  currentTxn,
  dispatch,
  excludeF2S,
  excludeReports,
  getTransactionDetailsMPProps,
  isDirty,
  manager,
  modelIdToLink,
  performTransactionAction,
  resetToEdit,
  saveTransactions,
  setIsBill,
  showUnsavedRecurringDialog,
  txn,
  workflowDispatch,
}: RecurringTransactionWorkflowParams): RecurringTransactionActions {
  const startRecurringSelect = useCallback(
    () => workflowDispatch(workflowActions.startRecurringSelect()),
    [workflowDispatch],
  );

  const exitRecurringSelect = useCallback(
    () => workflowDispatch(workflowActions.exitRecurringSelect()),
    [workflowDispatch],
  );

  const markRecurring = useCallback(() => {
    if (!create && isDirty) {
      showUnsavedRecurringDialog();
      return;
    }
    workflowDispatch(workflowActions.showRecurringOptions());
  }, [create, isDirty, showUnsavedRecurringDialog, workflowDispatch]);

  const stopRecurring = useCallback(() => workflowDispatch(workflowActions.stopRecurringOptions()), [workflowDispatch]);

  const completeCreatedTxnRecurringFlow = useCallback(
    (createdTxn: CashFlowTransaction, recurringIdToLink?: string) => {
      if (!createdTxn.id || !transactionsUtils.isExistingTxn(createdTxn)) return;

      manager.fireAction('updateBaseTxn', createdTxn);
      workflowDispatch(workflowActions.stopWaitingForTxn());
      workflowDispatch(workflowActions.clearTxnClientId());

      if (recurringIdToLink) {
        log.debug('\nLinking txn');
        workflowDispatch(workflowActions.clearModelIdToLink());
        workflowDispatch(workflowActions.showCompletion('Transaction linked to recurring series'));
        performTransactionAction(
          { action: 'link-to-scheduled-txn', id: createdTxn.id, otherId: recurringIdToLink },
          { undo: { userMessage: 'Transaction linked.' } },
        );
        return;
      }

      workflowDispatch(workflowActions.startEditRecurring());
      const payload = { clientId: uuidv4().toUpperCase(), id: createdTxn.id };
      dispatch(
        scheduledTransactionsActions.createBillScoutSchedule(payload, {
          props: {
            sourceIsExcludedFromF2S: excludeF2S,
            sourceIsExcludedFromReports: excludeReports,
            sourceBusinessId: businessId ?? undefined,
          },
        }),
      );
    },
    [businessId, dispatch, excludeF2S, excludeReports, manager, performTransactionAction, workflowDispatch],
  );

  const createTxn = useCallback(
    (recurringIdToLink?: string) => {
      log.debug('\nCreating txn!');
      const clientId = uuidv4().toUpperCase();
      workflowDispatch(workflowActions.startWaitingForTxn(clientId));
      saveTransactions([currentTxn.set('clientId', clientId)], undefined, (resources) => {
        const createdTxn = resources.find(
          (resource) => resource.clientId === clientId || resource.resource.clientId === clientId,
        )?.resource;

        if (createdTxn) {
          completeCreatedTxnRecurringFlow(createdTxn, recurringIdToLink);
        }
      });
    },
    [completeCreatedTxnRecurringFlow, currentTxn, saveTransactions, workflowDispatch],
  );

  const createRecurring = useCallback(() => {
    exitRecurringSelect();
    if (create) {
      createTxn();
    } else {
      workflowDispatch(workflowActions.startEditRecurring());
      const payload = { clientId: uuidv4().toUpperCase(), id: txn.id || '' };
      dispatch(
        scheduledTransactionsActions.createBillScoutSchedule(payload, {
          props: {
            sourceIsExcludedFromF2S: excludeF2S,
            sourceIsExcludedFromReports: excludeReports,
            sourceBusinessId: businessId ?? undefined,
          },
        }),
      );
    }
  }, [
    businessId,
    create,
    createTxn,
    dispatch,
    excludeF2S,
    excludeReports,
    exitRecurringSelect,
    txn.id,
    workflowDispatch,
  ]);

  const saveRecurring = useCallback(() => {
    log.debug('\nSaving recurring');
    workflowDispatch(workflowActions.saveRecurringComplete());
  }, [workflowDispatch]);

  const updateRecurring = useCallback(() => {
    log.debug('\nUpdating recurring');
    workflowDispatch(workflowActions.updateRecurringComplete());
  }, [workflowDispatch]);

  const closeRecurring = useCallback(
    (dirty = false, force?: boolean) => {
      if (force) {
        closeAll();
      } else if (dirty) {
        workflowDispatch(workflowActions.updateRecurringComplete());
      } else {
        workflowDispatch(workflowActions.closeEditSeries());
      }
    },
    [closeAll, workflowDispatch],
  );

  const onCloseReview = useCallback(() => {
    workflowDispatch(workflowActions.closeReview());
    closeAll();
  }, [closeAll, workflowDispatch]);

  const dismissReview = useCallback(() => {
    workflowDispatch(workflowActions.closeReview());
  }, [workflowDispatch]);

  const linkTxn = useCallback(
    (recurringId: string | undefined) => {
      const txnToUse = currentTxn;
      const modelId = recurringId || modelIdToLink;
      if (txnToUse?.id && modelId) {
        log.debug('\nLinking txn');
        workflowDispatch(workflowActions.clearModelIdToLink());
        workflowDispatch(workflowActions.stopWaitingForTxn());
        workflowDispatch(workflowActions.showCompletion('Transaction linked to recurring series'));
        performTransactionAction(
          { action: 'link-to-scheduled-txn', id: txnToUse.id, otherId: modelId },
          { undo: { userMessage: 'Transaction linked.' } },
        );
      }
    },
    [currentTxn, modelIdToLink, performTransactionAction, workflowDispatch],
  );

  const linkRecurring = useCallback(
    (item: ScheduledTransaction) => {
      log.debug('\nLinking recurring');
      tracker.track(
        tracker.events.txnUpdatedInFTSRegister,
        getTransactionDetailsMPProps({ action: 'linkRecurring', context: 'details-dialog', txn: currentTxn }),
      );

      if (create) {
        createTxn(item.id);
        workflowDispatch(workflowActions.setModelIdToLink(item.id));
      } else {
        linkTxn(item.id);
      }
      resetToEdit();
    },
    [create, createTxn, currentTxn, getTransactionDetailsMPProps, linkTxn, resetToEdit, workflowDispatch],
  );

  const doOneTimeBill = useCallback(() => {
    workflowDispatch(workflowActions.showCompletion('Marked as one time bill'));
    setIsBill(true);
    setTimeout(() => saveTransactions([currentTxn.set('isBill', true)]), 0);
  }, [currentTxn, saveTransactions, setIsBill, workflowDispatch]);

  return {
    closeRecurring,
    createRecurring,
    dismissReview,
    doOneTimeBill,
    exitRecurringSelect,
    linkRecurring,
    linkTxn,
    markRecurring,
    onCloseReview,
    saveRecurring,
    startRecurringSelect,
    stopRecurring,
    updateRecurring,
  };
}
